import CompanyInformationForm from '@/modules/verify/company-info';
import PageContainer from '@/components/layout/page-container';
import ContactForm from '@/modules/verify/contact-info';
import ExpertiseForm from '@/modules/verify/expertise-and-niche';
import MediaFilesPage from '@/modules/verify/media';
import { useGetOrganisation } from '@/services/organisations/queries';
import { useAuthStore } from '@/store/auth';
import { showToast } from '@/components/ui/show-toast';
import { Navigate } from 'react-router-dom';
import { Skeleton } from '@/components/ui/skeleton';
import { useGetMapCategories, useGetOrganisationMapData } from '@/services/mapdata/queries';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useQueryState } from 'nuqs';
import { Building2, Contact, Briefcase, Image } from 'lucide-react';

const EditOrganisationPage = () => {
  const [view, setView] = useQueryState('view', { defaultValue: 'company-info' });

  const activeWorkspace = useAuthStore((state) => state.activeWorkspace);
  const organisationId = useAuthStore((state) => state.user?.organisation?.id);

  if (!activeWorkspace) {
    showToast('error', 'Something went wrong!!! Your organisation does not have any workspaces');
    return <Navigate to="/sign-in" replace />;
  }

  const { data: organisation, isPending: isLoadingOrganisation } = useGetOrganisation(organisationId ?? '', {
    enabled: !!organisationId,
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 5 * 60 * 1000, // 5 minutes
  })

  // get map data
  const { data: mapData, isPending: isLoadingMapData } = useGetOrganisationMapData(activeWorkspace, organisationId ?? '', {
    enabled: !!organisationId,
    staleTime: 1 * 60 * 1000, // 1 minute
  })

  // get map categories
  const { data: mapCategories, isPending: isLoadingMapCategories } = useGetMapCategories(activeWorkspace, undefined, {
    enabled: !!activeWorkspace,
    staleTime: 3 * 60 * 1000, // 3 minutes
    gcTime: 3 * 60 * 1000, // 3 minutes
  })

  if (isLoadingOrganisation) {
    return (
      <PageContainer>
        <div className='flex items-center justify-center h-[60vh]'>
          <Skeleton className='w-full h-full rounded-lg' />
        </div>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <div className='rounded bg-white p-6 min-h-screen'>
        <div className='mb-6'>
          <h1 className='text-2xl font-bold text-gray-900'>Edit Organisation Profile</h1>
          <p className='text-gray-600 mt-1'>Update your organisation information and settings</p>
        </div>

        <Tabs value={view || 'company-info'} onValueChange={setView} className='w-full'>
          <TabsList className='inline-flex h-11 items-center justify-start rounded-lg bg-muted p-1 text-muted-foreground'>
            <TabsTrigger 
              value='company-info' 
              className='inline-flex items-center justify-center whitespace-nowrap rounded-md px-4 py-2 text-sm font-medium ring-offset-background transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 data-[state=active]:bg-background data-[state=active]:text-foreground data-[state=active]:shadow-sm'
            >
              <Building2 className='mr-2 h-4 w-4' />
              Company Info
            </TabsTrigger>
            <TabsTrigger 
              value='contact-info'
              className='inline-flex items-center justify-center whitespace-nowrap rounded-md px-4 py-2 text-sm font-medium ring-offset-background transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 data-[state=active]:bg-background data-[state=active]:text-foreground data-[state=active]:shadow-sm'
            >
              <Contact className='mr-2 h-4 w-4' />
              Contact Info
            </TabsTrigger>
            <TabsTrigger 
              value='expertise'
              className='inline-flex items-center justify-center whitespace-nowrap rounded-md px-4 py-2 text-sm font-medium ring-offset-background transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 data-[state=active]:bg-background data-[state=active]:text-foreground data-[state=active]:shadow-sm'
            >
              <Briefcase className='mr-2 h-4 w-4' />
              Expertise & Niche
            </TabsTrigger>
            <TabsTrigger 
              value='media'
              className='inline-flex items-center justify-center whitespace-nowrap rounded-md px-4 py-2 text-sm font-medium ring-offset-background transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 data-[state=active]:bg-background data-[state=active]:text-foreground data-[state=active]:shadow-sm'
            >
              <Image className='mr-2 h-4 w-4' />
              Media
            </TabsTrigger>
          </TabsList>

          <TabsContent value='company-info' className='mt-6 ml-4'>
            <CompanyInformationForm data={organisation} componentType='edit' />
          </TabsContent>

          <TabsContent value='contact-info' className='mt-6 ml-4'>
            <ContactForm data={organisation} componentType='edit' />
          </TabsContent>

          <TabsContent value='expertise' className='mt-6 ml-4'>
            {!isLoadingMapCategories && !isLoadingMapData && mapCategories && mapData && organisationId ? (
              <ExpertiseForm mapCategories={mapCategories.data} mapData={mapData} organisationId={organisationId} componentType='edit' />
            ) : (
              <div className='flex items-center justify-center h-40'>
                <Skeleton className='w-full h-full rounded-lg' />
              </div>
            )}
          </TabsContent>

          <TabsContent value='media' className='mt-6 ml-4'>
            <MediaFilesPage data={organisation} componentType='edit' />
          </TabsContent>
        </Tabs>
      </div>
    </PageContainer>
  )
}

export default EditOrganisationPage